import Service, { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import { task } from "ember-concurrency";
import { action } from "@ember/object";
import { queryRecord } from "@warp-drive/legacy/compat/builders";
import ENV from "glesys-controlpanel/config/environment";

export default class SessionService extends Service {
  @service ajax;
  @service http;
  @service router;
  @service store;
  @service storage;

  @tracked currentUser;
  #context;
  @tracked sidebarOpen = false;

  get isAuthenticated() {
    return !!this.currentUser;
  }

  get basicToken() {
    let token = this.storage.get("glesys:token");

    if (token) {
      try {
        window.atob(token);
        return token;
      } catch {
        // Failed to base64 decode so token is invalid, maybe an old JWT token
        // make sure we remove it
        this.storage.remove("glesys:token");
      }
    }

    if (this.context && this.apiKey) {
      return btoa(`${this.context}:${this.apiKey}`);
    }

    return "";
  }

  get context() {
    return this.#context || this.currentUser?.username || this.storage.get("glesys:context");
  }

  set context(context) {
    this.#context = context;
  }

  get apiKey() {
    return this.storage.get("glesys:apikey");
  }

  tokenFor(context) {
    let token = window.atob(this.basicToken);
    let apiKey = token.slice(token.indexOf(":") + 1);

    return window.btoa(`${context}:${apiKey}`);
  }

  open = task(async (username, password, otp, verificationCode) => {
    let body = {
      username,
      password,
    };

    if (otp) {
      body.otp = otp;
    }

    if (verificationCode) {
      body.verificationcode = verificationCode;
    }

    let response = await this.http.request({
      url: `${ENV.apiBaseUri}/user/login`,
      method: "POST",
      body,
    });

    let context = response.content.login.username;
    let apiKey = response.content.login.apikey;
    let token = window.btoa(`${context}:${apiKey}`);

    this.storage.set("glesys:token", token);

    return this.fetch.perform();
  });

  fetch = task(async () => {
    try {
      let { content: user } = await this.store.request(queryRecord("user", {}));

      this.currentUser = user;

      await this.currentUser.get("organizations").reload();
    } catch {
      this.storage.remove("glesys:token");
      this.storage.remove("glesys:context");
      this.storage.remove("glesys:apikey");
      this.currentUser = null;
    }
    return this.currentUser;
  });

  close = task(async (status) => {
    await this.ajax.post(`${ENV.apiBaseUri}/user/logout`).catch(() => {});

    this.storage.remove("glesys:token");
    this.storage.remove("glesys:context");
    this.storage.remove("glesys:apikey");
    this.currentUser = null;

    await this.router.transitionTo("login", { queryParams: { status } });

    this.store.unloadAll();
  });

  requireAuthentication(transition) {
    if (!this.isAuthenticated) {
      this.attemptedTransition = transition;
      this.router.transitionTo("login");
    }
  }

  @action
  openSidebar() {
    this.sidebarOpen = true;
  }

  @action
  closeSidebar() {
    this.sidebarOpen = false;
  }
}
